import React, {useState, useEffect, useContext} from "react";

import * as Yup from "yup";
import {useHistory} from "react-router-dom";
import {Link as RouterLink} from "react-router-dom";
import {toast} from "react-toastify";
import {Formik, Form, Field} from "formik";

import Avatar from "@material-ui/core/Avatar";
import Button from "@material-ui/core/Button";
import CssBaseline from "@material-ui/core/CssBaseline";
import TextField from "@material-ui/core/TextField";
import Link from "@material-ui/core/Link";
import Grid from "@material-ui/core/Grid";
import Box from "@material-ui/core/Box";
import LockOutlinedIcon from "@material-ui/icons/LockOutlined";
import Typography from "@material-ui/core/Typography";
import {makeStyles, useTheme} from "@material-ui/core/styles";
import Container from "@material-ui/core/Container";
import {i18n} from "../../translate/i18n";
import CircularProgress from "@material-ui/core/CircularProgress";
import {Helmet} from "react-helmet";

import {openApi} from "../../services/api";
import toastError from "../../errors/toastError";
import useSettings from "../../hooks/useSettings";
import ColorModeContext from "../../layout/themeContext";
import { getBackendUrl } from "../../config";
import { createGradient } from "../../utils/themeColors";
import { getSignupIntent, clearSignupIntent } from "../../utils/signupIntent";

const useStyles = makeStyles(theme => {
    const {mode} = theme;
    return ({
        root: {
            width: "100vw",
            height: "100vh",
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            padding: "0",
            boxSizing: "border-box",
            overflow: "auto",
            position: "relative",
            backgroundColor: theme.palette.background ? theme.palette.background.default : (theme.palette.type === "light" ? "#f5f5f5" : "#303030"),
            backgroundImage: "none",
            backgroundRepeat: "no-repeat",
            backgroundSize: "cover",
            backgroundPosition: "center center",

            [theme.breakpoints.down("sm")]: {
                flexDirection: "column",
            },
        },
        containerSignup: {
            padding: "16px",
            width: "calc(100% - 32px)",
            zIndex: 10,
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%)",
            position: "absolute",
            maxWidth: "420px",
            margin: "0 auto",

            [theme.breakpoints.down("sm")]: {
                width: "calc(100% - 40px)",
                maxWidth: "90%",
            },

            [theme.breakpoints.down("xs")]: {
                width: "calc(100% - 32px)",
                maxWidth: "85%",
            },
        },
        paper: {
            backgroundColor: mode === "light" ? "rgba(255, 255, 255, 0.95)" : "rgba(35, 38, 40, 0.95)",
            backdropFilter: "blur(20px)",
            WebkitBackdropFilter: "blur(20px)",
            boxShadow: `
        0 20px 40px rgba(0, 0, 0, 0.1),
        0 1px 0 rgba(255, 255, 255, 0.2) inset,
        0 0 0 1px rgba(255, 255, 255, 0.1)
      `,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            padding: "30px 25px",
            borderRadius: "20px",
            maxWidth: "420px",
            width: "100%",
            margin: "0 auto",
            border: "1px solid rgba(255, 255, 255, 0.2)",
            animation: "$slideInUp 0.8s cubic-bezier(0.4, 0, 0.2, 1)",
            position: "relative",
            boxSizing: "border-box",

            [theme.breakpoints.down("sm")]: {
                animation: "$slideInUp 0.8s cubic-bezier(0.4, 0, 0.2, 1)",
                borderRadius: "16px",
                padding: "25px 20px",
                boxSizing: "border-box",
                width: "100%",
            },

            [theme.breakpoints.down("xs")]: {
                padding: "20px 15px",
                borderRadius: "14px",
                boxShadow: `0 15px 30px rgba(0, 0, 0, 0.12)`,
                boxSizing: "border-box",
                width: "100%",
            },
        },
        avatar: {
            margin: theme.spacing(1),
            backgroundColor: theme.palette.primary.main,
        },
        form: {
            width: "100%",
            marginTop: theme.spacing(3),
        },
        submit: {
            margin: theme.spacing(3, 0, 2),
            background: createGradient(theme.palette.primary.main, theme.palette.secondary?.main, 45),
            color: "white",
            borderRadius: "12px",
            padding: "12px 0",
            fontSize: "16px",
            fontWeight: 600,
            textTransform: "none",
            boxShadow: `0 4px 12px ${theme.palette.primary.main}40`,
            border: "none",
            transition: "all 0.3s ease",
            "&:hover": {
                background: createGradient(theme.palette.primary.dark || theme.palette.primary.main, theme.palette.secondary?.dark || theme.palette.secondary?.main, 45),
                transform: "translateY(-2px)",
                boxShadow: `0 6px 20px ${theme.palette.primary.main}66`,
            },
            "&:active": {
                transform: "translateY(0)",
            },
        },
        backButton: {
            margin: theme.spacing(1, 0),
            color: theme.palette.primary.main,
            textTransform: "none",
            fontWeight: 500,
        },
        planBox: {
            padding: "16px",
            borderRadius: "12px",
            backgroundColor: mode === "light" ? "rgba(79, 70, 229, 0.08)" : "rgba(79, 70, 229, 0.2)",
            border: "1px solid rgba(79, 70, 229, 0.3)",
            textAlign: "left",
            marginBottom: theme.spacing(2),
        },
        planTitle: {
            fontWeight: 600,
            color: theme.palette.primary.main,
        },
        planDetails: {
            marginTop: "4px",
            color: theme.palette.text.secondary,
        },
        documentMasked: {
            fontFamily: 'monospace',
            letterSpacing: '1px',
            fontSize: '14px',
            color: theme.palette.text.secondary,
            marginTop: theme.spacing(1),
        },
        "@keyframes slideInUp": {
            from: {
                opacity: 0,
                transform: "translateY(30px)",
            },
            to: {
                opacity: 1,
                transform: "translateY(0)",
            },
        },
    })
});

const Step1Schema = Yup.object().shape({
    country: Yup.string().required("Country is required"),
    email: Yup.string().email("Invalid email").required("Required"),
    document: Yup.string()
        .test('document-format', 'CPF deve ter 11 dígitos ou CNPJ deve ter 14 dígitos', function (value) {
            if (!value) return false;
            const numbers = value.replace(/\D/g, '');
            return numbers.length === 11 || numbers.length === 14;
        })
        .required("CPF ou CNPJ é obrigatório"),
});

const Step2Schema = Yup.object().shape({
    name: Yup.string()
        .min(2, "Too Short!")
        .max(50, "Too Long!")
        .required("Required"),
    companyName: Yup.string()
        .min(2, "Too Short!")
        .max(50, "Too Long!")
        .required("Required"),
    password: Yup.string().min(8, "Senha deve ter no mínimo 8 caracteres").required("Senha é obrigatória"),
    phone: Yup.string().required("Required"),
    postalCode: Yup.string().required("CEP é obrigatório"),
    street: Yup.string().required("Endereço é obrigatório"),
    number: Yup.string().required("Número é obrigatório"),
    city: Yup.string().required("Cidade é obrigatória"),
    state: Yup.string().required("UF é obrigatória"),
});

const formatCurrency = (amountCents, currency) => {
    try {
        const locale = currency === 'BRL' ? 'pt-BR' : 'en-US';
        return new Intl.NumberFormat(locale, {
            style: 'currency',
            currency: currency || 'BRL',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format((amountCents || 0) / 100);
    } catch {
        return `${currency || 'BRL'} ${((amountCents || 0) / 100).toFixed(2)}`;
    }
};

const getBillingCycleLabel = (cycle) => {
    if (!cycle) return '';
    const key = `plans.form.billingCycle.${cycle}`;
    const translated = i18n.t(key);
    return translated !== key ? translated : cycle;
};

const SignUp = () => {
    const classes = useStyles();
    const theme = useTheme();
    const history = useHistory();
    const {colorMode} = useContext(ColorModeContext) || {};
    const {appLogoFavicon, appName, mode} = colorMode || {};
    const signupIntent = getSignupIntent();
    const planPublicId = String(signupIntent?.planPublicId || "").trim();

    const [selectedPlan, setSelectedPlan] = useState(null);
    const [planLoading, setPlanLoading] = useState(true);
    const [step, setStep] = useState(1);
    const [identifyResponse, setIdentifyResponse] = useState(null);
    const [identifyLoading, setIdentifyLoading] = useState(false);
    const [idempotencyKey] = useState(() => `signup-idemp-${Date.now()}-${Math.random().toString(16).slice(2)}`);
    const [backgroundLight, setBackgroundLight] = useState("");
    const [backgroundDark, setBackgroundDark] = useState("");

    let companyId = null;

    const initialStep1State = {
        country: "BR",
        email: "",
        document: "",
        planPublicId,
    };

    const initialStep2State = {
        name: "",
        companyName: "",
        password: "",
        phone: "",
        postalCode: "",
        street: "",
        number: "",
        complement: "",
        district: "",
        city: "",
        state: "",
        planPublicId,
    };

    const [step1Values, setStep1Values] = useState(initialStep1State);
    const [step2Values, setStep2Values] = useState(initialStep2State);
    const [duplicateError, setDuplicateError] = useState("");
    const [signupDuplicateError, setSignupDuplicateError] = useState("");

    const {getPublicSetting} = useSettings();

    useEffect(() => {
        if (!planPublicId) {
            toast.info("Selecione um plano comercial para continuar o cadastro.");
            history.push("/planos");
            return;
        }

        let mounted = true;
        setPlanLoading(true);
        openApi.get("/public/billing/plans")
            .then(({ data }) => {
                if (!mounted) return;
                const list = Array.isArray(data?.data) ? data.data : [];
                const found = list.find(p => String(p.id || p.publicId || "") === planPublicId);
                if (found) {
                    setSelectedPlan(found);
                } else {
                    toast.error("Plano comercial não encontrado ou indisponível.");
                    history.push("/planos");
                }
                setPlanLoading(false);
            })
            .catch(() => {
                if (!mounted) return;
                toast.error("Não foi possível validar o plano selecionado.");
                history.push("/planos");
            });

        return () => { mounted = false; };
    }, [history, planPublicId]);

    useEffect(() => {
        
        getPublicSetting("userCreation")
            .then((data) => {
                if (data === "disabled") {
                    toast.error(i18n.t("signup.toasts.disabled"));
                    history.push("/login");
                }
            })
            .catch((error) => {
                console.log("Error reading setting", error);
            });

        getPublicSetting("appLogoBackgroundLight", companyId)
            .then((bgLight) => {
                if (bgLight) {
                    const backendUrl = getBackendUrl();
                    const fullUrl = backendUrl + "/public/" + bgLight;
                    setBackgroundLight(fullUrl);
                } else {
                    setBackgroundLight("");
                }
            })
            .catch((err) => {
                setBackgroundLight("");
            });

        getPublicSetting("appLogoBackgroundDark", companyId)
            .then((bgDark) => {
                if (bgDark) {
                    const backendUrl = getBackendUrl();
                    const fullUrl = backendUrl + "/public/" + bgDark;
                    setBackgroundDark(fullUrl);
                } else {
                    setBackgroundDark("");
                }
            })
            .catch((err) => {
                setBackgroundDark("");
            });
    }, [history, companyId]);

    const formatCpf = (value) => {
        const numbers = value.replace(/\D/g, '');
        const limitedNumbers = numbers.slice(0, 11);

        if (limitedNumbers.length <= 3) return limitedNumbers;
        if (limitedNumbers.length <= 6) return `${limitedNumbers.slice(0, 3)}.${limitedNumbers.slice(3)}`;
        if (limitedNumbers.length <= 9) return `${limitedNumbers.slice(0, 3)}.${limitedNumbers.slice(3, 6)}.${limitedNumbers.slice(6)}`;
        return `${limitedNumbers.slice(0, 3)}.${limitedNumbers.slice(3, 6)}.${limitedNumbers.slice(6, 9)}-${limitedNumbers.slice(9)}`;
    };

    const formatCnpj = (value) => {
        const numbers = value.replace(/\D/g, '');
        const limitedNumbers = numbers.slice(0, 14);

        if (limitedNumbers.length <= 2) return limitedNumbers;
        if (limitedNumbers.length <= 5) return `${limitedNumbers.slice(0, 2)}.${limitedNumbers.slice(2)}`;
        if (limitedNumbers.length <= 8) return `${limitedNumbers.slice(0, 2)}.${limitedNumbers.slice(2, 5)}.${limitedNumbers.slice(5)}`;
        if (limitedNumbers.length <= 12) return `${limitedNumbers.slice(0, 2)}.${limitedNumbers.slice(2, 5)}.${limitedNumbers.slice(5, 8)}/${limitedNumbers.slice(8)}`;
        return `${limitedNumbers.slice(0, 2)}.${limitedNumbers.slice(2, 5)}.${limitedNumbers.slice(5, 8)}/${limitedNumbers.slice(8, 12)}-${limitedNumbers.slice(12)}`;
    };

    const formatDocument = (value) => {
        const numbers = value.replace(/\D/g, '');
        if (numbers.length <= 11) {
            return formatCpf(value);
        } else {
            return formatCnpj(value);
        }
    };

    const maskDocument = (document, documentType) => {
        const clean = document.replace(/\D/g, "");
        if (documentType === 'cpf' || clean.length === 11) {
            return `${clean.substring(0, 3)}.***.***-${clean.substring(9)}`;
        }
        if (documentType === 'cnpj' || clean.length === 14) {
            return `${clean.substring(0, 2)}.***.***/****-${clean.substring(12)}`;
        }
        return "***.***.***-**";
    };

    const handleIdentify = async (values) => {
        setIdentifyLoading(true);
        setDuplicateError("");
        try {
            const response = await openApi.post('/public/billing/identify', {
                planPublicId,
                country: values.country,
                email: values.email.trim().toLowerCase(),
                document: values.document.replace(/\D/g, '')
            });

            setIdentifyResponse(response.data);
            setStep1Values(values);
            setStep(2);

            if (response.data.prefill?.legalName) {
                setStep2Values(prev => ({
                    ...prev,
                    companyName: response.data.prefill.legalName,
                    postalCode: response.data.prefill.postalCode || "",
                    street: response.data.prefill.street || "",
                    number: response.data.prefill.number || "",
                    complement: response.data.prefill.complement || "",
                    district: response.data.prefill.district || "",
                    city: response.data.prefill.city || "",
                    state: response.data.prefill.state || ""
                }));
            }

            return true;
        } catch (e) {
            const error = e.response?.data?.error;
            const code = e.response?.data?.code;
            if (error === "REGISTRATION_ALREADY_EXISTS" || code === "EMAIL_EXISTS" || code === "DOCUMENT_EXISTS") {
                setDuplicateError("REGISTRATION_ALREADY_EXISTS");
            } else {
                toastError(e);
            }
            return false;
        } finally {
            setIdentifyLoading(false);
        }
    };

    const handleSignUp = async (values) => {
        setSignupDuplicateError("");
        try {
            const payload = {
                planPublicId,
                company: {
                    name: values.companyName,
                    document: identifyResponse.document,
                    phone: values.phone.replace(/\D/g, ""),
                    timezone: "America/Sao_Paulo",
                    country: identifyResponse.country
                },
                address: {
                    postalCode: values.postalCode.replace(/\D/g, ""),
                    street: values.street,
                    number: values.number,
                    complement: values.complement || "",
                    district: values.district || "",
                    city: values.city,
                    state: values.state.toUpperCase()
                },
                administrator: {
                    name: values.name,
                    email: identifyResponse.email,
                    password: values.password
                },
                acceptances: {
                    termsVersion: "2026.1",
                    privacyVersion: "2026.1",
                    accepted: true
                }
            };

            const response = await openApi.post("/public/billing/signup", payload, {
                headers: {
                    "idempotency-key": idempotencyKey
                }
            });

            const { onboardingId, statusToken } = response.data || {};
            if (onboardingId && statusToken) {
                sessionStorage.setItem(`ticketwhats_status_token_${onboardingId}`, statusToken);
                clearSignupIntent();
                toast.success(i18n.t("signup.toasts.success"));
                history.push(`/billing/signup/${onboardingId}/checkout`);
            } else {
                toast.error("Não foi possível gerar a ordem de pagamento.");
            }
        } catch (err) {
            const error = err.response?.data?.error;
            const code = err.response?.data?.code;
            if (error === "REGISTRATION_ALREADY_EXISTS" || code === "EMAIL_EXISTS" || code === "DOCUMENT_EXISTS") {
                setSignupDuplicateError("REGISTRATION_ALREADY_EXISTS");
            } else {
                toastError(err);
            }
        }
    };

    const handleBack = () => {
        setStep(1);
        setSignupDuplicateError("");
    };

    let finalBackground;

    const hasValidLightBg = backgroundLight && backgroundLight.trim() !== '';
    const hasValidDarkBg = backgroundDark && backgroundDark.trim() !== '';

    if (mode === "light") {
        if (hasValidLightBg) {
            finalBackground = `url(${backgroundLight})`;
        } else {
            finalBackground = theme.palette.background ? theme.palette.background.default : "#f5f5f5";
        }
    } else {
        if (hasValidDarkBg) {
            finalBackground = `url(${backgroundDark})`;
        } else {
            finalBackground = theme.palette.background ? theme.palette.background.default : "#303030";
        }
    }

    if (step === 1) {
        return (
            <React.Fragment>
                <Helmet>
                    <title>{appName || "TicketWhats - Cadastro"}</title>
                    <link rel="icon" href={appLogoFavicon || "/default-favicon.ico"}/>
                </Helmet>

                <div
                    className={classes.root}
                    style={{
                        backgroundImage: typeof finalBackground === "string" && finalBackground.includes("url(")
                            ? finalBackground
                            : "none"
                    }}
                >
                    <Container
                        component="main"
                        maxWidth={false}
                        className={classes.containerSignup}
                        style={{
                            zIndex: 10,
                            position: "absolute",
                            left: "50%",
                            top: "50%",
                            transform: "translate(-50%, -50%)",
                            boxSizing: "border-box",
                            margin: "0 auto"
                        }}
                    >
                        <CssBaseline/>
                        <div className={classes.paper}>
                            <Avatar className={classes.avatar}>
                                <LockOutlinedIcon/>
                            </Avatar>
                            <Typography component="h1" variant="h5">
                                {i18n.t("signup.title")}
                            </Typography>
                            
                             <div className={classes.planBox}>
                                <Typography variant="subtitle2" className={classes.planTitle}>
                                    {i18n.t("signup.plan.selected") || "Plano Selecionado"}: {planLoading ? "..." : (selectedPlan ? selectedPlan.name : "Nenhum plano")}
                                </Typography>
                                {selectedPlan && (
                                    <Typography variant="body2" className={classes.planDetails}>
                                        {formatCurrency(selectedPlan.amountCents, selectedPlan.currency)} / {getBillingCycleLabel(selectedPlan.billingCycle)} · {selectedPlan.users} usuários · {selectedPlan.connections} conexões
                                    </Typography>
                                )}
                            </div>

                            <Formik
                                initialValues={step1Values}
                                enableReinitialize={true}
                                validationSchema={Step1Schema}
                                onSubmit={async (values, actions) => {
                                    try {
                                        await handleIdentify(values);
                                    } finally {
                                        actions.setSubmitting(false);
                                    }
                                }}
                            >
                                {({touched, errors, isSubmitting, setFieldValue, values}) => (
                                    <Form className={classes.form}>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12}>
                                                <Field
                                                    as={TextField}
                                                    select
                                                    variant="outlined"
                                                    fullWidth
                                                    id="country"
                                                    label={i18n.t("signup.country.label") || "País"}
                                                    name="country"
                                                    value={values.country}
                                                    onChange={e => setFieldValue('country', e.target.value)}
                                                    SelectProps={{
                                                        native: true,
                                                    }}
                                                >
                                                    <option value="BR">Brasil</option>
                                                    <option value="US">Estados Unidos</option>
                                                    <option value="OTHER">Outro</option>
                                                </Field>
                                            </Grid>

                                            <Grid item xs={12}>
                                                <Field
                                                    as={TextField}
                                                    variant="outlined"
                                                    fullWidth
                                                    id="email"
                                                    label={i18n.t("signup.form.email")}
                                                    name="email"
                                                    error={touched.email && Boolean(errors.email)}
                                                    helperText={touched.email && errors.email}
                                                    autoComplete="email"
                                                    inputProps={{style: {textTransform: 'lowercase'}}}
                                                    onChange={(e) => {
                                                        setFieldValue('email', e.target.value);
                                                        setDuplicateError("");
                                                    }}
                                                />
                                            </Grid>

                                            <Grid item xs={12}>
                                                <Field
                                                    as={TextField}
                                                    variant="outlined"
                                                    fullWidth
                                                    id="document"
                                                    label={i18n.t("signup.document.label")}
                                                    name="document"
                                                    error={touched.document && Boolean(errors.document)}
                                                    helperText={(touched.document && errors.document) || i18n.t("signup.document.helper")}
                                                    autoComplete="document"
                                                    inputProps={{
                                                        maxLength: 18,
                                                        style: {textTransform: 'none'}
                                                    }}
                                                    autoFocus
                                                    onChange={(e) => {
                                                        let value = e.target.value;
                                                        const formattedValue = formatDocument(value);
                                                        setFieldValue('document', formattedValue);
                                                        setDuplicateError("");
                                                    }}
                                                />
                                            </Grid>
                                        </Grid>

                                        {duplicateError && (
                                            <Grid item xs={12} style={{ textAlign: 'left' }}>
                                                <div style={{
                                                    padding: '12px',
                                                    borderRadius: '8px',
                                                    backgroundColor: theme.palette.error.main + '15',
                                                    border: `1px solid ${theme.palette.error.main}40`,
                                                }}>
                                                    <Typography variant="body2" style={{ color: theme.palette.error.dark, fontWeight: 500 }}>
                                                        {i18n.t("signup.errors.registrationAlreadyExists")}
                                                    </Typography>
                                                    <Typography variant="body2" style={{ color: theme.palette.text.secondary, marginTop: '4px' }}>
                                                        {i18n.t("signup.errors.loginLink")}{' '}
                                                        <Link
                                                            href="#"
                                                            variant="body2"
                                                            component={RouterLink}
                                                            to="/login"
                                                            style={{ fontWeight: 600 }}
                                                        >
                                                            {i18n.t("signup.errors.loginButton")}
                                                        </Link>
                                                    </Typography>
                                                </div>
                                            </Grid>
                                        )}

                                        <Button
                                            type="submit"
                                            fullWidth
                                            variant="contained"
                                            color="primary"
                                            className={classes.submit}
                                            disabled={isSubmitting || identifyLoading}
                                        >
                                            {identifyLoading ? "Validando seus dados..." : i18n.t("signup.buttons.continue")}
                                        </Button>

                                        <Grid container>
                                            <Grid item>
                                                <Link
                                                    href="#"
                                                    variant="body2"
                                                    component={RouterLink}
                                                    to="/login"
                                                >
                                                    {i18n.t("signup.buttons.login")}
                                                </Link>
                                            </Grid>
                                        </Grid>
                                    </Form>
                                )}
                            </Formik>
                        </div>
                        <Box mt={5}>{/* <Copyright /> */}</Box>
                    </Container>
                </div>
            </React.Fragment>
        );
    }

    if (step === 2) {
        const documentType = identifyResponse?.documentType || 'cpf';
        const maskedDocument = maskDocument(identifyResponse?.document || '', documentType);

        return (
            <React.Fragment>
                <Helmet>
                    <title>{appName || "TicketWhats - Complete seu cadastro"}</title>
                    <link rel="icon" href={appLogoFavicon || "/default-favicon.ico"}/>
                </Helmet>

                <div
                    className={classes.root}
                    style={{
                        backgroundImage: typeof finalBackground === "string" && finalBackground.includes("url(")
                            ? finalBackground
                            : "none"
                    }}
                >
                    <Container
                        component="main"
                        maxWidth={false}
                        className={classes.containerSignup}
                        style={{
                            zIndex: 10,
                            position: "absolute",
                            left: "50%",
                            top: "50%",
                            transform: "translate(-50%, -50%)",
                            boxSizing: "border-box",
                            margin: "0 auto"
                        }}
                    >
                        <CssBaseline/>
                        <div className={classes.paper}>
                            <Avatar className={classes.avatar}>
                                <LockOutlinedIcon/>
                            </Avatar>
                            <Typography component="h1" variant="h5">
                                {identifyResponse?.prefill?.legalName ? i18n.t("signup.step2.reviewTitle") : i18n.t("signup.step2.manualTitle")}
                            </Typography>

                            {identifyResponse?.prefill?.legalName && (
                                <Typography variant="body2" color="textSecondary" style={{ marginBottom: theme.spacing(2) }}>
                                    {i18n.t("signup.step2.foundDataMessage")}
                                </Typography>
                            )}

                            <div className={classes.planBox}>
                                <Typography variant="subtitle2" className={classes.planTitle}>
                                    {i18n.t("signup.plan.selected") || "Plano Selecionado"}: {selectedPlan ? selectedPlan.name : "Nenhum plano"}
                                </Typography>
                                {selectedPlan && (
                                    <Typography variant="body2" className={classes.planDetails}>
                                        {formatCurrency(selectedPlan.amountCents, selectedPlan.currency)} / {getBillingCycleLabel(selectedPlan.billingCycle)}
                                    </Typography>
                                )}
                            </div>

                            <div style={{ textAlign: 'left', width: '100%', marginBottom: theme.spacing(2) }}>
                                <Typography variant="subtitle2" style={{ fontWeight: 600, color: theme.palette.text.primary }}>
                                    Documento
                                </Typography>
                                <Typography variant="body2" className={classes.documentMasked}>
                                    {maskedDocument}
                                </Typography>
                                <Typography variant="body2" style={{ color: theme.palette.text.secondary, fontSize: '12px' }}>
                                    {documentType.toUpperCase()}
                                </Typography>
                            </div>

                            <div style={{ textAlign: 'left', width: '100%', marginBottom: theme.spacing(2) }}>
                                <Typography variant="subtitle2" style={{ fontWeight: 600, color: theme.palette.text.primary }}>
                                    {i18n.t("signup.form.email")}
                                </Typography>
                                <Typography variant="body2" style={{ color: theme.palette.text.secondary }}>
                                    {identifyResponse?.email || identifyResponse?.emailNorm || ""}
                                </Typography>
                            </div>

                            {signupDuplicateError && (
                                <Grid item xs={12} style={{ textAlign: 'left' }}>
                                    <div style={{
                                        padding: '12px',
                                        borderRadius: '8px',
                                        backgroundColor: theme.palette.error.main + '15',
                                        border: `1px solid ${theme.palette.error.main}40`,
                                        marginBottom: theme.spacing(2),
                                    }}>
                                        <Typography variant="body2" style={{ color: theme.palette.error.dark, fontWeight: 500 }}>
                                            {i18n.t("signup.errors.registrationAlreadyExists")}
                                        </Typography>
                                        <Typography variant="body2" style={{ color: theme.palette.text.secondary, marginTop: '4px' }}>
                                            {i18n.t("signup.errors.loginLink")}{' '}
                                            <Link
                                                href="#"
                                                variant="body2"
                                                component={RouterLink}
                                                to="/login"
                                                style={{ fontWeight: 600 }}
                                            >
                                                {i18n.t("signup.errors.loginButton")}
                                            </Link>
                                        </Typography>
                                    </div>
                                </Grid>
                            )}

                            <Formik
                                initialValues={step2Values}
                                enableReinitialize={true}
                                validationSchema={Step2Schema}
                                onSubmit={async (values, actions) => {
                                    try {
                                        await handleSignUp(values);
                                    } finally {
                                        actions.setSubmitting(false);
                                    }
                                }}
                            >
                                {({touched, errors, isSubmitting, setFieldValue}) => (
                                    <Form className={classes.form}>
                                        <Grid container spacing={2}>
                                            {documentType === 'cnpj' && (
                                                <Grid item xs={12}>
                                                    <Field
                                                        as={TextField}
                                                        variant="outlined"
                                                        fullWidth
                                                        id="companyName"
                                                        label={i18n.t("signup.form.companyName")}
                                                        name="companyName"
                                                        error={touched.companyName && Boolean(errors.companyName)}
                                                        helperText={touched.companyName && errors.companyName}
                                                        autoComplete="companyName"
                                                    />
                                                </Grid>
                                            )}

                                            {documentType === 'cnpj' && identifyResponse?.prefill?.tradeName && (
                                                <Grid item xs={12}>
                                                    <Typography variant="subtitle2" style={{ fontWeight: 600, color: theme.palette.text.primary, textAlign: 'left', width: '100%' }}>
                                                        {i18n.t("signup.form.tradeName")}
                                                    </Typography>
                                                    <Typography variant="body2" style={{ textAlign: 'left', width: '100%', marginTop: '-8px', marginBottom: '8px' }}>
                                                        {identifyResponse.prefill.tradeName}
                                                    </Typography>
                                                </Grid>
                                            )}

                                            {documentType === 'cpf' && (
                                                <Grid item xs={12}>
                                                    <Field
                                                        as={TextField}
                                                        variant="outlined"
                                                        fullWidth
                                                        id="companyName"
                                                        label={i18n.t("signup.form.personName")}
                                                        name="companyName"
                                                        error={touched.companyName && Boolean(errors.companyName)}
                                                        helperText={touched.companyName && errors.companyName}
                                                        autoComplete="name"
                                                    />
                                                </Grid>
                                            )}

                                            <Grid item xs={12}>
                                                <Field
                                                    as={TextField}
                                                    autoComplete="name"
                                                    name="name"
                                                    error={touched.name && Boolean(errors.name)}
                                                    helperText={touched.name && errors.name}
                                                    variant="outlined"
                                                    fullWidth
                                                    id="name"
                                                    label={i18n.t("signup.form.name")}
                                                />
                                            </Grid>

                                            <Grid item xs={12}>
                                                <Field
                                                    as={TextField}
                                                    variant="outlined"
                                                    fullWidth
                                                    id="postalCode"
                                                    label="CEP"
                                                    name="postalCode"
                                                    error={touched.postalCode && Boolean(errors.postalCode)}
                                                    helperText={touched.postalCode && errors.postalCode}
                                                    inputProps={{ maxLength: 9 }}
                                                    onChange={(e) => {
                                                        let value = e.target.value.replace(/\D/g, '').slice(0, 8);
                                                        if (value.length > 5) {
                                                            value = `${value.slice(0,5)}-${value.slice(5)}`;
                                                        }
                                                        setFieldValue('postalCode', value);
                                                    }}
                                                />
                                            </Grid>

                                            <Grid item xs={12}>
                                                <Field
                                                    as={TextField}
                                                    variant="outlined"
                                                    fullWidth
                                                    id="street"
                                                    label="Logradouro"
                                                    name="street"
                                                    error={touched.street && Boolean(errors.street)}
                                                    helperText={touched.street && errors.street}
                                                />
                                            </Grid>

                                            <Grid container spacing={2}>
                                                <Grid item xs={6}>
                                                    <Field
                                                        as={TextField}
                                                        variant="outlined"
                                                        fullWidth
                                                        id="number"
                                                        label="Número"
                                                        name="number"
                                                        error={touched.number && Boolean(errors.number)}
                                                        helperText={touched.number && errors.number}
                                                    />
                                                </Grid>
                                                <Grid item xs={6}>
                                                    <Field
                                                        as={TextField}
                                                        variant="outlined"
                                                        fullWidth
                                                        id="complement"
                                                        label="Complemento"
                                                        name="complement"
                                                        error={touched.complement && Boolean(errors.complement)}
                                                        helperText={touched.complement && errors.complement}
                                                    />
                                                </Grid>
                                            </Grid>

                                            <Grid item xs={12}>
                                                <Field
                                                    as={TextField}
                                                    variant="outlined"
                                                    fullWidth
                                                    id="district"
                                                    label="Bairro"
                                                    name="district"
                                                    error={touched.district && Boolean(errors.district)}
                                                    helperText={touched.district && errors.district}
                                                />
                                            </Grid>

                                            <Grid container spacing={2}>
                                                <Grid item xs={6}>
                                                    <Field
                                                        as={TextField}
                                                        variant="outlined"
                                                        fullWidth
                                                        id="city"
                                                        label="Cidade"
                                                        name="city"
                                                        error={touched.city && Boolean(errors.city)}
                                                        helperText={touched.city && errors.city}
                                                    />
                                                </Grid>
                                                <Grid item xs={6}>
                                                    <Field
                                                        as={TextField}
                                                        variant="outlined"
                                                        fullWidth
                                                        id="state"
                                                        label="UF"
                                                        name="state"
                                                        error={touched.state && Boolean(errors.state)}
                                                        helperText={touched.state && errors.state}
                                                        inputProps={{ maxLength: 2, style: { textTransform: 'uppercase' } }}
                                                        onChange={(e) => setFieldValue('state', e.target.value.toUpperCase())}
                                                    />
                                                </Grid>
                                            </Grid>

                                            <Grid item xs={12}>
                                                <Field
                                                    as={TextField}
                                                    variant="outlined"
                                                    fullWidth
                                                    name="password"
                                                    error={touched.password && Boolean(errors.password)}
                                                    helperText={touched.password && errors.password}
                                                    label={i18n.t("signup.form.password")}
                                                    type="password"
                                                    id="password"
                                                    autoComplete="current-password"
                                                />
                                            </Grid>

                                            <Grid item xs={12}>
                                                <Field
                                                    as={TextField}
                                                    variant="outlined"
                                                    fullWidth
                                                    id="phone"
                                                    label={i18n.t("signup.form.phone")}
                                                    name="phone"
                                                    autoComplete="phone"
                                                />
                                            </Grid>
                                        </Grid>

                                        <Button
                                            type="button"
                                            fullWidth
                                            variant="text"
                                            className={classes.backButton}
                                            onClick={handleBack}
                                        >
                                            {i18n.t("signup.step2.backButton")}
                                        </Button>

                                        <Button
                                            type="submit"
                                            fullWidth
                                            variant="contained"
                                            color="primary"
                                            className={classes.submit}
                                            disabled={isSubmitting}
                                        >
                                            {i18n.t("signup.buttons.submit")}
                                        </Button>

                                        <Grid container>
                                            <Grid item>
                                                <Link
                                                    href="#"
                                                    variant="body2"
                                                    component={RouterLink}
                                                    to="/login"
                                                >
                                                    {i18n.t("signup.buttons.login")}
                                                </Link>
                                            </Grid>
                                        </Grid>
                                    </Form>
                                )}
                            </Formik>
                        </div>
                        <Box mt={5}>{/* <Copyright /> */}</Box>
                    </Container>
                </div>
            </React.Fragment>
        );
    }

    return null;
};

export default SignUp;